/**
 * useSchoolRequestWithForm - School Registration Request Hook
 *
 * Modern hook using react-hook-form + Zod for school registration request form.
 * Replaces useSchoolRequestForm.ts (manual state management).
 *
 * Features:
 * - react-hook-form for state management
 * - Zod validation via schoolRequestSchema
 * - shadcn Form components integration
 * - Server-side error handling
 * - Direct API submission (no data transformation!)
 * - Conflict detection (duplicate SIIIR codes)
 * - Status-based logic (PENDING/REJECTED/APPROVED)
 * - Create vs update logic
 * - Token refresh and redirect after success
 *
 * Pattern: Zod schema → react-hook-form → Direct API submission
 *
 * Usage:
 * ```tsx
 * const {
 *   form,
 *   onSubmit,
 *   loading,
 *   approvalStatus,
 *   rejectReason,
 *   schoolStatus,
 * } = useSchoolRequestWithForm();
 * ```
 *
 * Created: 2025-11-02
 * Version: 1.0
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast, Slide } from 'react-toastify';
import { useEffect, useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

import { schoolRequestsApi } from '../api/school-requests.api';
import { schoolsApi } from '@/domains/schools/api/schools.api';
import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';
import { updateFailureToast, updateSuccessToast } from '../../../toast';
import { delay } from '../../../shared/utils';
import { resolvePostAuthTarget } from '@/domains/auth/utils/resolve-post-auth-target';
import { pasiInscriereActive } from '@/domains/auth/utils/pasi-inscriere';
import type { UserInfo } from '@/providers/auth-provider.types';
import {
  schoolRequestSchema,
  type SchoolRequestFormData,
  type SchoolRequestFormInput,
} from '../validation/school-request.schemas';
import type { SchoolRequestDto } from '../api/school-requests.api';
import type { SchoolRegistrationStatusDto } from '@/api/generated/types.gen';
import { getErrorMessage, getErrorStatus, getFieldErrors } from '@/core/api/response-handler';
import { applyServerFieldErrors, handleInvalidSubmit } from '@/shared/utils';
import { trackSchoolRequestFailed, trackSchoolRequestSubmitted } from '@/telemetry/posthog-events';

/* =============================================================================
   TYPES
   ============================================================================= */

/* =============================================================================
   HOOK
   ============================================================================= */

export function useSchoolRequestWithForm() {
  const { handleTokenRefresh } = useAuthProviderV2();
  const { PATHS, PAGES, ROLES, getUserHomePage } = useRoutesProvider();

  // p8: dupa un token nou, tinta o decide RESOLVERUL, nu un `PATHS[...]` scris aici --
  // acelasi tipar ca useLoginWithForm/useOtpVerify/etc. Cu wizardul pornit (video +
  // protocol active), o cerere noua ateriza pe pasul de video, nu pe pagina de asteptare.
  //
  // SE-07: functia asta e `onSuccess` pe `createMutation`/`updateMutation` — React Query
  // ASTEAPTA `onSuccess` inainte sa rezolve `mutateAsync(...)`. Daca ceva de aici arunca (refresh
  // de token esuat, decodare, resolver), `mutateAsync` RESPINGE desi POST-ul deja a reusit si
  // cererea deja exista in baza -- `handleSubmit` de mai jos prindea acea respingere in `catch` si
  // arata un toast de eroare peste o salvare reusita. Navigarea post-submisie nu are voie sa
  // rescrie rezultatul salvarii; de aici incolo se prinde local, nu se mai re-arunca.
  const navigheazaDupaSubmisie = async () => {
    // C1: formularul se marcheaza „curat" INAINTE de navigarea pe care o produce tot el.
    // Refresh-ul de mai jos aduce un JWT cu `hasPendingSchoolRequest: true`, deci
    // `redirectTo` se schimba si `AuthenticatedLayout` cheama `navigate()` singur. Garda
    // din `useFormNavigation` (`useBlocker`, pe `isDirty`) prinde EXACT acel redirect
    // automat -- nu o actiune a omului -- si deschide dialogul „Modificari nesalvate"
    // peste o salvare reusita. `useBeforeUnload`, armat tot pe `isDirty`, produce acelasi
    // lucru ca prompt NATIV la `window.location.href` de mai jos.
    //
    // 🔴 `isDirty` era deja `true` INAINTE de submit (`submitDisabled = !isDirty || ...`,
    // SchoolRequestFormPage.tsx), deci submit-ul nu creeaza dirty-ul: creeaza NAVIGAREA.
    // Pe calea de succes nu exista niciun `form.reset()` -- singurul e in efectul care
    // depinde de `requestData`, iar cheia `['school-requests','own']` nu e invalidata
    // nicaieri in domeniu, deci efectul nu re-ruleaza. Un singur `reset` aici le repara
    // pe amandoua, pentru ca ruleaza inaintea `delay(1000)` / `window.location.href`.
    // NU se face prin invalidarea query-ului: ar re-incarca datele exact cand omul pleaca.
    //
    // 🔴 `reset(form.getValues())`, NU `reset(undefined, { keepValues: true })`. Amandoua
    // pastreaza valorile pe ecran, dar numai primul chiar face formularul „curat" — si
    // diferenta nu se vede in tipuri, nici in teste, doar in browser. Masurat pe
    // react-hook-form 7.85.0, in `_reset` (`dist/index.esm.mjs:3206`):
    //
    //   isDirty: isEmptyResetValues ? false
    //          : keepDirty        ? _formState.isDirty
    //          : keepValues       ? _getDirty()          // <-- varianta respinsa
    //          : !!(keepDefaultValues && !deepEqual(formValues, _defaultValues));
    //
    // Cu `values` dat, `keepDefaultValues` e fals, deci ultima ramura da `false`. Cu
    // `keepValues`, se ajunge la `_getDirty()`, adica `!deepEqual(_formValues, _defaultValues)`
    // — iar `_defaultValues` NU se atinge (`updatedValues = formValues ? clone : _defaultValues`,
    // deci ramane `defaultValues` din `useForm`), iar `_formValues` sunt datele completate de
    // om. Comparatia e deci „a modificat omul ceva fata de gol?" → `true`. Adica exact
    // `isDirty` pe care voiam sa-l stingem.
    //
    // Prins de sonda pe build-ul 3, care a aratat dialogul exact ca pe build-ul 1. Nu de
    // teste: `npm run test` si clichetul de tipuri au trecut pe amandoua variantele.
    form.reset(form.getValues());
    try {
      const tokenNou = await handleTokenRefresh(true);
      const decodat = tokenNou ? jwtDecode<UserInfo>(tokenNou) : null;
      const tinta = decodat
        ? resolvePostAuthTarget(decodat, null, {
            PATHS,
            PAGES,
            ROLES,
            getUserHomePage,
            pasiInscriere: pasiInscriereActive(),
          })
        : null;
      await delay(1000);
      window.location.href = tinta ?? PATHS[PAGES.ACCOUNT_UNAUTHORIZED_TEACHER];
    } catch (error) {
      console.error('School request post-submit navigation error (cererea tot s-a salvat):', error);
      window.location.href = PATHS[PAGES.ACCOUNT_UNAUTHORIZED_TEACHER];
    }
  };

  // State for approval status and conflict detection
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<string | null>(null);
  const [schoolStatus, setSchoolStatus] = useState<SchoolRegistrationStatusDto | undefined>(undefined);

  // ============================================================================
  // FORM SETUP
  // ============================================================================

  // Initialize react-hook-form with Zod validation
  const form = useForm<SchoolRequestFormInput, unknown, SchoolRequestFormData>({
    resolver: zodResolver(schoolRequestSchema),
    defaultValues: {
      codSiiirUnitate: '',
      numeInstitutieInvatamant: '',
      cui: '',
      telefon: '',
      // P1-1, corectie dupa verdict S1: NU 0. react-hook-form's Controller
      // calculeaza `field.value` ca `get(formValues, name, defaultValue)` --
      // ori de cate ori valoarea curenta a campului e `undefined`, cade pe
      // DEFAULT-ul inregistrat aici, nu ramane `undefined`. Cu `0` inregistrat,
      // golirea campului (onChange(undefined)) parea sa functioneze in codul
      // care calculeaza `next`, dar la randarea urmatoare `field.value` revenea
      // tacut la 0 -- reprodus live, cu log pe field.value la fiecare randare.
      numarElevi: undefined,
      numeReprezentatLegal: '',
      codSe: '',
      adresa: {
        judet: '',
        oras: '',
        comuna: '',
        sat: '',
        strada: '',
        numar: '',
        codPostal: '',
        bloc: '',
        scara: '',
        etaj: '',
        apartament: '',
      },
      // Fara valoare implicita: livrarea se ALEGE explicit (radio), niciuna preselectata. `true` aici
      // ar reintroduce exact bifa pre-bifata pe care owner-ul a scos-o (18.09).
      adresaLivrareAceeasiCuScoala: undefined,
      adresaLivrare: undefined,
      detineRecipiente: false,
      saptamanaVerdeStart: '',
      saptamanaVerdeEnd: '',
      saptamanaVerdeNecunoscuta: false,
      prinPrezentaConfirmamInscrierea: false,
      amFostInformati: false,
      confirmFaptulCaReprezentatulLegal: false,
      prinPrezentaConfirmCaAmCititInformarea: false,
      confirmCaInCazulInCareProiectul: false,
      consimtPentruPastrareaDatelorPersonale: false,
      confirmCaAmAcordulDePrincipiu: false,
      amCititSiSuntDeAcord: false,
      ecoticPoateFolosiImagini: false,
      acordGrupWhatsapp: false,
    },
    mode: 'onBlur', // Validate on blur for better UX
  });

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  // Load existing request or default template
  const {
    data: requestData,
    isLoading: loadingRequest,
    isError: errorRequest,
    refetch: refetchRequest,
  } = useQuery({
    queryKey: ['school-requests', 'own'],
    queryFn: async () => {
      // `null` = 204, nu exista inca o cerere proprie: stare de business legitima -> se
      // trece la formularul implicit. Orice EROARE (inclusiv 404, care pe ruta asta nu
      // mai inseamna „fara cerere", ci „ruta lipseste") NU se ascunde in spatele unui
      // formular gol (precedent: use-editions.ts:27-35) - se arunca mai departe, ca
      // `isError` sa devina true si formularul sa NU se afiseze ca si cum ar fi o cerere
      // noua, cand de fapt cererea reala n-a putut fi citita.
      const cerere = await schoolRequestsApi.getOwn();
      if (cerere) {
        return { type: 'existing', data: cerere };
      }

      // Get default template for new request
      try {
        const data = await schoolRequestsApi.getDefault();
        if (data && Object.keys(data).length > 0) {
          return { type: 'default', data };
        }
      } catch (error) {
        if (getErrorStatus(error) !== 404) throw error;
      }

      // Return empty template
      return { type: 'empty', data: {} };
    },
    retry: false,
  });

  // Populate form when data loads
  useEffect(() => {
    if (requestData?.data && Object.keys(requestData.data).length > 0) {
      const data = requestData.data as SchoolRequestDto;

      // Set approval status if existing request
      if (requestData.type === 'existing') {
        setApprovalStatus(data.approvalStatus || null);
        setRejectReason(data.adminRejectReason || null);
      }

      // Reset form with loaded data
      // Convert null to undefined for optional backend-managed fields (Zod expects undefined, not null)
      // Convert null to empty strings for address fields (Zod string schema doesn't accept null)
      form.reset({
        id: data.id ?? undefined,
        codSiiirUnitate: data.codSiiirUnitate || '',
        numeInstitutieInvatamant: data.numeInstitutieInvatamant || '',
        cui: data.cui || '',
        telefon: data.telefon || '',
        // P1-1: `|| 0` ar reinregistra acelasi 0 ca default la fiecare reset,
        // recreand exact defectul de mai sus pentru o cerere existenta cu
        // numarElevi neinregistrat inca. `undefined` lasa campul gol (si
        // validarea Zod cere oricum minim 1).
        numarElevi: data.numarElevi || undefined,
        numeReprezentatLegal: data.numeReprezentatLegal || '',
        codSe: data.codSe || '',
        adresa: data.adresa ? {
          id: data.adresa.id ?? undefined,
          judet: data.adresa.judet ?? '',
          oras: data.adresa.oras ?? '',
          comuna: data.adresa.comuna ?? '',
          sat: data.adresa.sat ?? '',
          strada: data.adresa.strada ?? '',
          numar: data.adresa.numar ?? '',
          codPostal: data.adresa.codPostal ?? '',
          bloc: data.adresa.bloc ?? '',
          scara: data.adresa.scara ?? '',
          etaj: data.adresa.etaj ?? '',
          apartament: data.adresa.apartament ?? '',
        } : {
          judet: '',
          oras: '',
          comuna: '',
          sat: '',
          strada: '',
          numar: '',
          codPostal: '',
          bloc: '',
          scara: '',
          etaj: '',
          apartament: '',
        },
        // O cerere deja salvata are o alegere; una fara (`null`/lipsa, sablon nou) ramane NEALEASA.
        adresaLivrareAceeasiCuScoala: data.adresaLivrareAceeasiCuScoala ?? undefined,
        // Populate adresaLivrare based on the delivery choice (nealeasa → nimic de populat)
        adresaLivrare: data.adresaLivrareAceeasiCuScoala == null
          ? undefined
          : data.adresaLivrareAceeasiCuScoala
          ? (data.adresa ? {  // Same as school → copy from school address (no ID)
              judet: data.adresa.judet ?? '',
              oras: data.adresa.oras ?? '',
              comuna: data.adresa.comuna ?? '',
              sat: data.adresa.sat ?? '',
              strada: data.adresa.strada ?? '',
              numar: data.adresa.numar ?? '',
              codPostal: data.adresa.codPostal ?? '',
              bloc: data.adresa.bloc ?? '',
              scara: data.adresa.scara ?? '',
              etaj: data.adresa.etaj ?? '',
              apartament: data.adresa.apartament ?? '',
            } : undefined)
          : (data.adresaLivrare ? {  // Different from school → use existing delivery address
              id: data.adresaLivrare.id ?? undefined,
              judet: data.adresaLivrare.judet ?? '',
              oras: data.adresaLivrare.oras ?? '',
              comuna: data.adresaLivrare.comuna ?? '',
              sat: data.adresaLivrare.sat ?? '',
              strada: data.adresaLivrare.strada ?? '',
              numar: data.adresaLivrare.numar ?? '',
              codPostal: data.adresaLivrare.codPostal ?? '',
              bloc: data.adresaLivrare.bloc ?? '',
              scara: data.adresaLivrare.scara ?? '',
              etaj: data.adresaLivrare.etaj ?? '',
              apartament: data.adresaLivrare.apartament ?? '',
            } : undefined),
        detineRecipiente: data.detineRecipiente ?? false,
        // Tipul generat spune `Date`, dar pe sarma e string ISO (LocalDate) -- cast, nu conversie
        // reala, acelasi tipar ca `TierFormDialog.availableFrom/To`.
        saptamanaVerdeStart: (data.saptamanaVerdeStart as unknown as string) || '',
        saptamanaVerdeEnd: (data.saptamanaVerdeEnd as unknown as string) || '',
        prinPrezentaConfirmamInscrierea: data.prinPrezentaConfirmamInscrierea ?? false,
        amFostInformati: data.amFostInformati ?? false,
        confirmFaptulCaReprezentatulLegal: data.confirmFaptulCaReprezentatulLegal ?? false,
        prinPrezentaConfirmCaAmCititInformarea: data.prinPrezentaConfirmCaAmCititInformarea ?? false,
        confirmCaInCazulInCareProiectul: data.confirmCaInCazulInCareProiectul ?? false,
        consimtPentruPastrareaDatelorPersonale: data.consimtPentruPastrareaDatelorPersonale ?? false,
        confirmCaAmAcordulDePrincipiu: data.confirmCaAmAcordulDePrincipiu ?? false,
        amCititSiSuntDeAcord: data.amCititSiSuntDeAcord ?? false,
        ecoticPoateFolosiImagini: data.ecoticPoateFolosiImagini ?? false,
        acordGrupWhatsapp: data.acordGrupWhatsapp ?? false,
        professorId: data.professorId ?? undefined,
        editionId: data.editionId ?? undefined,
        companyId: data.companyId ?? undefined,
      });
    }
  }, [requestData, form]);

  // ============================================================================
  // DELIVERY ADDRESS CHECKBOX WATCHER
  // ============================================================================

  // Watch checkbox AND school address to populate delivery address when either changes
  const sameAsSchoolCheckbox = form.watch('adresaLivrareAceeasiCuScoala');
  const schoolAddress = form.watch('adresa');

  useEffect(() => {
    // Run when checkbox is checked AND school address exists (has judet populated)
    if (sameAsSchoolCheckbox && schoolAddress && schoolAddress.judet) {
      // When checkbox is checked (same as school), copy school address to delivery address
      // This matches the pattern from WasteCollectionFormPage
      // Remove ID to avoid detached entity errors on backend
      const { id, ...addressWithoutId } = schoolAddress;
      form.setValue('adresaLivrare', {
        judet: addressWithoutId.judet ?? '',
        oras: addressWithoutId.oras ?? '',
        comuna: addressWithoutId.comuna ?? '',
        sat: addressWithoutId.sat ?? '',
        strada: addressWithoutId.strada ?? '',
        numar: addressWithoutId.numar ?? '',
        codPostal: addressWithoutId.codPostal ?? '',
        bloc: addressWithoutId.bloc ?? '',
        scara: addressWithoutId.scara ?? '',
        etaj: addressWithoutId.etaj ?? '',
        apartament: addressWithoutId.apartament ?? '',
      }, { shouldDirty: true });
    }
  }, [sameAsSchoolCheckbox, schoolAddress, form]);

  // ============================================================================
  // PRE-CHECK: STAREA SCOLII IN EDITIA CURENTA
  // ============================================================================
  //
  // Inainte se interoga raportul de admin (GET /api/school-requests/report), care:
  //   - nu e scopat pe editie, deci o cerere din editia trecuta arata ca un conflict curent;
  //   - devine admin-only, deci ar fi raspuns 403 pentru profesor;
  //   - intorcea si emailul profesorului, pe care il afisam in UI.
  //
  // Acum se cheama GET /api/schools/registration-status, care raspunde exact la intrebarea
  // formularului si expune doar numele profesorului.

  // Watch for SIIIR code changes to detect conflicts
  const watchedSiiirCode = form.watch('codSiiirUnitate');

  useEffect(() => {
    if (!watchedSiiirCode || watchedSiiirCode.length < 3) {
      setSchoolStatus(undefined);
      return;
    }

    // Debounce + anulare: efectul se declanseaza la fiecare tasta, iar fara garda
    // un raspuns intarziat de la un cod vechi putea suprascrie rezultatul celui nou.
    let anulat = false;
    const timer = setTimeout(() => {
      schoolsApi
        .getRegistrationStatus(watchedSiiirCode)
        .then((status) => {
          if (!anulat) setSchoolStatus(status);
        })
        .catch(() => {
          // Pre-check informativ: esecul nu blocheaza formularul.
          if (!anulat) setSchoolStatus(undefined);
        });
    }, 400);

    return () => {
      anulat = true;
      clearTimeout(timer);
    };
  }, [watchedSiiirCode]);

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  // «Depune totuși cererea» traieste in pagina; ajunge aici prin `handleSubmit`, ca evenimentul de
  // succes (din `onSuccess`, inaintea navigarii care reincarca pagina) sa-l poata citi.
  const depusaTotusi = useRef(false);
  const raporteazaSubmisia = (isResubmit: boolean, cerere: SchoolRequestFormData) => {
    trackSchoolRequestSubmitted({
      isResubmit,
      schoolStatus: schoolStatus?.status,
      submittedAnyway: depusaTotusi.current,
      judet: cerere.adresa?.judet,
    });
  };

  // Create new school request
  const createMutation = useMutation({
    mutationFn: async (data: SchoolRequestFormData) => {
      // Direct API submission - NO data transformation needed!
      // `saptamanaVerdeNecunoscuta` e UI-only (SE-10/11) -- fara corespondent in DTO.
      const { saptamanaVerdeNecunoscuta: _saptamanaVerdeNecunoscuta, ...cerere } = data;
      return schoolRequestsApi.create(cerere as any);
    },
    onSuccess: (_raspuns, cerere) => {
      raporteazaSubmisia(false, cerere);
      return navigheazaDupaSubmisie();
    },
  });

  // Update existing school request (resubmit rejected)
  const updateMutation = useMutation({
    mutationFn: async (data: SchoolRequestFormData) => {
      // Direct API submission - NO data transformation needed!
      // `saptamanaVerdeNecunoscuta` e UI-only (SE-10/11) -- fara corespondent in DTO.
      const { saptamanaVerdeNecunoscuta: _saptamanaVerdeNecunoscuta, ...cerere } = data;
      return schoolRequestsApi.update(cerere as any);
    },
    onSuccess: (_raspuns, cerere) => {
      raporteazaSubmisia(true, cerere);
      return navigheazaDupaSubmisie();
    },
  });

  // ============================================================================
  // FORM HANDLERS
  // ============================================================================

  // Form submit handler
  const handleSubmit = async (data: SchoolRequestFormData, optiuni?: { depuneTotusi?: boolean }) => {
    depusaTotusi.current = optiuni?.depuneTotusi ?? false;
    // Nicio scoala nu mai BLOCHEAZA trimiterea din frontend (decizie owner, 18.09): nici cea deja
    // aprobata in editia curenta. Cererea de preluare se salveaza si o arbitreaza echipa
    // proiectului; dialogul din pagina informeaza, nu interzice. ⚠️ Perechea din backend (relaxarea
    // garzii de 409 din `SchoolRequestService`) trebuie sa ajunga odata cu asta — fara ea,
    // serverul refuza in continuare, iar profesorul vede refuzul ca toast la salvare.

    const toastId = toast.loading('Se salvează cererea...', { transition: Slide });

    try {
      // Determine create vs update based on ID presence
      if (data.id) {
        await updateMutation.mutateAsync(data);
      } else {
        await createMutation.mutateAsync(data);
      }

      updateSuccessToast('Cererea a fost salvată cu succes!', toastId);
    } catch (error: unknown) {
      console.error('School request submit error:', error);

      // Handle server-side validation errors
      const fieldErrors = getFieldErrors(error);
      trackSchoolRequestFailed('server', fieldErrors ? Object.keys(fieldErrors) : []);
      applyServerFieldErrors(error, form.setError);

      // Toastul spune CATE campuri sunt marcate, nu titlul brut al backendului
      // („Eroare la validarea datelor. Verifica datele trimise." e scris pentru
      // dezvoltatori). Ramane necesar: la 360px, primul camp invalid poate fi in
      // afara ecranului, si fara toast apasarea pe „Trimite" n-ar produce nimic vizibil.
      if (fieldErrors) {
        // Acelasi drum ca pentru orice submit respins (`handleInvalidSubmit`): scroll + focus pe
        // primul camp marcat, rezumat numarat, iar mesajele care nu se vad NICAIERI in pagina —
        // pentru ca backendul le-a pus pe campuri nerandate — intra in mesaj si nu se autoinchid.
        toast.dismiss(toastId);
        handleInvalidSubmit(
          fieldErrors,
          getErrorMessage(error, 'Salvare eșuată. Verificați datele introduse.')
        );
      } else {
        updateFailureToast(
          getErrorMessage(error, 'Salvare eșuată. Verificați datele introduse.'),
          toastId
        );
      }
    }
  };

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    form,
    onSubmit: form.handleSubmit((data) => handleSubmit(data)), // For <form onSubmit={onSubmit}>
    handleFormSubmit: handleSubmit, // For programmatic calls (e.g., confirmation dialogs)
    loading: createMutation.isPending || updateMutation.isPending || loadingRequest,
    // `loading` de mai sus include si submisia (create/update) -- nu poate servi drept
    // gardă pentru randarea formularului, altfel formularul ar disparea in timpul
    // salvarii. `loadingRequest` singur raspunde la intrebarea corecta: "stim inca
    // daca cererea e PENDING?" (P1-2 -- vezi randarea din pagina).
    loadingRequest,
    isDirty: form.formState.isDirty, // Track unsaved changes for navigation blocking
    approvalStatus,
    rejectReason,
    schoolStatus,
    requestData: requestData?.data, // Expose for read-only fields if needed
    isError: errorRequest,
    refetch: refetchRequest,
  };
}
